// Modules
import { connect } from 'react-redux';
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';

import { alerts } from 'modules/alerts/actions';
import CompanyNickname from 'components/models/company/company-nickname';
import SVG from 'components/widgets/icons/svg';
import TicketLink from 'components/models/ticket/ticket-link';
import Triage from 'services/data/triage';
import UserBadge from 'components/models/user/user-badge';
import * as ticketUtils from 'routes/ticket/utils/load-tickets';

import { AddToTicket, ResolveWithTime, Archive, PullIntoTicket } from './triage-options';
import AddToContactForm from './triage-options/forms/add-to-contact';
import AddToTicketForm from './triage-options/forms/add-to-ticket-form';
import ResolveWithTimeForm from './triage-options/forms/resolve-with-time-form';
import TriageTypeStatusBox from './triage-type-status-box';


class TriageItem extends React.Component {
  static propTypes = {
    children: PropTypes.node,
    triageItem: PropTypes.object.isRequired,
    updateTriageItem: PropTypes.func.isRequired,
    payloadOrStatus: PropTypes.node,
    itemOrigin: PropTypes.node,
    currentUser: PropTypes.object.isRequired,
    className: PropTypes.string,
    hideTicket: PropTypes.bool,
    onDashboard: PropTypes.bool,
    route: PropTypes.object
  };

  constructor() {
    super();

    this.addToTicket = this.addToTicket.bind(this);
    this.resolveWithTime = this.resolveWithTime.bind(this);
  }

  addToTicket(values) {
    return Triage.addToTicket(values, this.props)
      .then((triageItem) => {
        alerts.success({
          title: 'Added to ticket!',
          subtitle: <TicketLink ticket={triageItem.ticket} hideOnRouteMatch>View ticket</TicketLink>
        });
      })
      .then(() => this.props.triageItem.setOpen(false))
      .then(ticketUtils.loadTicketsExternal);
  }

  resolveWithTime(values) {
    return Triage.resolveWithTime(values, this.props)
      .then(() => this.props.triageItem.setOpen(false))
      .catch(alerts.catchError);
  }

  render() {
    const { triageItem, children, itemOrigin, payloadOrStatus, className, currentUser, updateTriageItem, hideTicket, route, onDashboard } = this.props;
    const { open, setOpen } = triageItem;
    const claimed = !_.isEmpty(triageItem.claimedBy) && triageItem.claimedBy;
    const archived = triageItem.status === 'Archived';
    const resolved = triageItem.status === 'Resolved';
    const ticketed = !_.isEmpty(triageItem.ticket);
    const matchedFrom = triageItem.matchedFrom;
    const hasCompany = !_.isEmpty(triageItem.company);
    const disableAutofill = _.get(triageItem, 'company.disableAutofill');
    const teamColor = _.get(triageItem, 'company.team.color', '#bdc3c7');
    const [, model, id] = _.split(route.pathname, '/');
    const inTicket = model === 'tickets' && id && !_.isNaN(Number(id));

    const companyNickname = (
      <div>
        { onDashboard && <span>(<CompanyNickname company={triageItem.company} />)</span> }
        { !onDashboard && <CompanyNickname company={triageItem.company} /> }
      </div>
    );

    const companyMarkup = (
      <div className="triage-company truncate">
        { hasCompany && companyNickname }
        { !matchedFrom &&
          <div className="triage-option-add-to-contact" onClick={() => setOpen('addToContact')}>
            <SVG src="svg/add-contact.svg" className="add-contact triage-option-icon" />
            <span>Connect Contact</span>
          </div>
        }
      </div>
    );

    return (
      <div className={classNames('triage-item', _.kebabCase(triageItem.type), className, _.kebabCase(triageItem.status), { claimed })}>
        <div className="triage-content">
          <TriageTypeStatusBox triageItem={triageItem} />
          <div className="triage-description">
            <div className="triage-contact truncate">
              <span className="untruncate">{itemOrigin}</span>
              { onDashboard && companyMarkup }
            </div>
            { !onDashboard && companyMarkup }
            <div className="triage-status truncate">
              { triageItem.status === 'Archived' ? 'Archived' : payloadOrStatus }
            </div>
          </div>
          <div className="triage-right-indicator">
            { triageItem.status === 'After Hours' && <SVG src="svg/moon.svg" className="after-hours-indicator" /> }
            { triageItem.autoResolved && <SVG src="svg/automatic.svg" className="auto-resolved-indicator" /> }
            <div>
              <UserBadge
                badgeForCurrentUser={claimed && triageItem.claimedBy.id === currentUser.id}
                user={triageItem.claimedBy}
                className="large"
              />
            </div>
          </div>
          { !onDashboard && <div className="triage-team-indicator" style={{ backgroundColor: teamColor }}></div> }
          { onDashboard && <div className="triage-team-indicator-dashboard" style={{ backgroundColor: teamColor }}></div> }
        </div>
        { !onDashboard &&
          <div className="triage-options">
            { ((!archived && !resolved) || hideTicket) && children }
            
            { !open && !archived && !hideTicket &&
              <div className={classNames('triage-secondary-options', { 'in-ticket': inTicket })}>
                { !ticketed && <PullIntoTicket triageItem={triageItem} updateTriageItem={updateTriageItem} /> }
                <AddToTicket triageItem={triageItem} />
                { !ticketed && <ResolveWithTime triageItem={triageItem} /> }
                { !ticketed && <Archive triageItem={triageItem} updateTriageItem={updateTriageItem} /> }
              </div>
            }

            { open === 'addToTicket' &&
              <AddToTicketForm
                item={triageItem}
                form={`add-to-ticket-${triageItem.type}-${triageItem.id}`}
                initialValues={{ company: hasCompany && !disableAutofill && triageItem.company.id }}
                onSubmit={this.addToTicket}
                addToTicket={this.addToTicket}
              />
            }

            { open === 'resolveWithTime' &&
              <ResolveWithTimeForm
                item={triageItem}
                form={`resolve-with-time-${triageItem.type}-${triageItem.id}`}
                initialValues={{ company: hasCompany && !disableAutofill && triageItem.company.id, timeReportDuration: 0.25 }}
                onSubmit={this.resolveWithTime}
              />
            }

            { open === 'addToContact' &&
              <AddToContactForm triageItem={triageItem} />
            }

            { archived &&
              <div className={classNames('triage-secondary-options', { 'in-ticket': inTicket })}>
                { !ticketed && <PullIntoTicket triageItem={triageItem} updateTriageItem={updateTriageItem} /> }
                { !ticketed &&
                  <div className="triage-option-header flex-grow" onClick={() => updateTriageItem({ id: triageItem.id, status: 'Active' })}>
                    Restore
                  </div>
                }
                { ticketed && <AddToTicket triageItem={triageItem} /> }
              </div>
            }
          </div>
        }
      </div>
    );
  }
}

// Exports
export default connect((state) => ({
  route: state.location
}))(TriageItem);
